import React from 'react';
import './AddOnChips.css';
import Tooltip from '../tooltip/Tooltip';
import { addOnColorStyle, hasAddOnColor } from '../../util/addOns';

// Labels up to this length are shown in full; longer ones end in an ellipsis and get a tooltip (tap on touch).
const FULL_LABEL_MAX = 24;

function AddOnChip({ label, tone, tooltip, colorKey }) {
  const text = String(label ?? '');
  const isLong = text.length > FULL_LABEL_MAX;
  const tip = tooltip || (isLong ? text : null);
  const isWarning = tone === 'warning';
  // The add-on color only tints; an exhausted counter stays the yellow warning pill.
  const isColored = !isWarning && hasAddOnColor(colorKey);
  const chip = (
    <span
      className={`addOnChip${isWarning ? ' isWarning' : ''}${isColored ? ' isColored' : ''}${isLong ? ' isLong' : ''}`}
      style={isColored ? addOnColorStyle(colorKey) : undefined}
      role="listitem"
    >
      {text}
    </span>
  );
  if (!tip) return chip;
  return <Tooltip text={tip} tapToShow className="addOnChipTip">{chip}</Tooltip>;
}

/**
 * Name chips of booked add-ons ("Leihausrüstung", "BBs ×2") or counters ("Leihausrüstung 5/8", warning tone when
 * exhausted) — build the items with util/addOns addOnLineItems / addOnCounterItems.
 * items: [{ key, label, tone?: 'neutral' | 'warning', tooltip?, colorKey? }]. Chips wrap onto further lines; a label
 * longer than ~24 characters ends in an ellipsis and shows its full text in a tooltip (`tooltip` overrides that text).
 * colorKey (AddOn.colorKey) tints the chip like a participant group chip; 'neutral' or missing = plain chip, and the
 * warning tone always wins (exhausted counter = yellow).
 * compact: smaller chips for list rows. The container is always rendered (empty without items), so grid cells
 * that host it keep their position.
 */
function AddOnChips({ items, compact = false, className = '', ariaLabel = 'Zusätze' }) {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  return (
    <div
      className={`addOnChips${compact ? ' isCompact' : ''}${className ? ` ${className}` : ''}`}
      role={list.length > 0 ? 'list' : undefined}
      aria-label={list.length > 0 ? ariaLabel : undefined}
    >
      {list.map((item, index) => (
        <AddOnChip
          key={item.key ?? index}
          label={item.label}
          tone={item.tone}
          tooltip={item.tooltip}
          colorKey={item.colorKey}
        />
      ))}
    </div>
  );
}

export default AddOnChips;
